import { useCallback, useEffect, useState } from 'react'
import { MapPin, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import { createBranch, fetchBranches, updateBranch } from '@/lib/api/branches'
import { BUSINESS_DAYS, defaultBusinessHours, normalizeBusinessHours } from '@/lib/businessHours'
import { PHONE_LOCAL_PLACEHOLDER } from '@/lib/phone'
import { cn } from '@/lib/utils'

const CURRENCIES = ['AED', 'SAR', 'USD', 'EUR', 'GBP', 'QAR', 'KWD', 'OMR', 'BHD']

function emptyForm() {
  return {
    name: '',
    code: '',
    currency_code: 'AED',
    country: 'AE',
    timezone: 'Asia/Dubai',
    phone: '',
    whatsapp: '',
    trn: '',
    address: '',
    map_url: '',
    is_active: true,
    business_hours: defaultBusinessHours(),
  }
}

function branchToForm(branch) {
  return {
    name: branch.name ?? '',
    code: branch.code ?? '',
    currency_code: branch.currency_code ?? 'AED',
    country: branch.country ?? 'AE',
    timezone: branch.timezone ?? 'Asia/Dubai',
    phone: branch.phone ?? '',
    whatsapp: branch.whatsapp ?? '',
    trn: branch.trn ?? '',
    address: branch.address ?? '',
    map_url: branch.map_url ?? '',
    is_active: Boolean(branch.is_active),
    business_hours: normalizeBusinessHours(branch.business_hours),
  }
}

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

export function BranchSetupPage() {
  const { refreshUser } = useAuth()
  const { activeBranchId } = useBranch()
  const [branches, setBranches] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [creating, setCreating] = useState(false)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState(emptyForm())

  const loadBranches = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const items = await fetchBranches()
      setBranches(items)
      return items
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to load branches.')
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let active = true

    async function init() {
      const items = await loadBranches()
      if (!active || items.length === 0) return

      const preferred = items.find((item) => item.id === activeBranchId) ?? items[0]
      setSelectedId(preferred.id)
      setForm(branchToForm(preferred))
      setCreating(false)
    }

    init()
    return () => {
      active = false
    }
  }, [activeBranchId, loadBranches])

  function selectBranch(branch) {
    setCreating(false)
    setSelectedId(branch.id)
    setForm(branchToForm(branch))
    setSuccess('')
    setError('')
  }

  function startCreate() {
    setCreating(true)
    setSelectedId(null)
    setForm(emptyForm())
    setSuccess('')
    setError('')
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
    setSuccess('')
  }

  function updateHours(day, field, value) {
    setForm((current) => ({
      ...current,
      business_hours: {
        ...current.business_hours,
        [day]: {
          ...current.business_hours[day],
          [field]: value,
        },
      },
    }))
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    const payload = {
      ...form,
      phone: form.phone || null,
      whatsapp: form.whatsapp || null,
      trn: form.trn || null,
      address: form.address || null,
      map_url: form.map_url || null,
    }

    try {
      if (creating) {
        const created = await createBranch(payload)
        const items = await loadBranches()
        await refreshUser()
        const saved = items.find((item) => item.id === created.id) ?? created
        selectBranch(saved)
        setSuccess('Branch created.')
      } else if (selectedId) {
        const updated = await updateBranch(selectedId, payload)
        setBranches((current) =>
          current.map((item) => (item.id === updated.id ? updated : item)),
        )
        setForm(branchToForm(updated))
        setSuccess('Branch saved.')
      }
    } catch (err) {
      const message =
        getFieldError(err, 'name') ??
        getFieldError(err, 'code') ??
        getFieldError(err, 'currency_code') ??
        err?.response?.data?.message ??
        'Unable to save branch.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <MapPin className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Branch setup</h1>
            <p className="text-sm text-muted-foreground">
              Locations, currency, country, and weekly business hours.
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={startCreate}>
          <Plus className="h-4 w-4" />
          New branch
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="card-surface p-3">
          <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Branches
          </p>
          {loading ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="space-y-1">
              {branches.map((branch) => {
                const isActive = !creating && selectedId === branch.id
                return (
                  <button
                    key={branch.id}
                    type="button"
                    onClick={() => selectBranch(branch)}
                    className={cn(
                      'flex w-full flex-col rounded-xl px-3 py-2 text-left text-sm transition-colors',
                      isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted/80',
                    )}
                  >
                    <span className="font-medium">{branch.name}</span>
                    <span className="text-[11px] text-muted-foreground">{branch.code}</span>
                  </button>
                )
              })}
              {creating ? (
                <div className="rounded-xl bg-primary/10 px-3 py-2 text-sm font-medium text-primary">
                  New branch
                </div>
              ) : null}
            </div>
          )}
        </aside>

        <form onSubmit={handleSubmit} className="card-surface p-6 lg:p-8">
          <div className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="branch-name" className="mb-1.5 block text-sm font-medium">
                  Branch name
                </label>
                <input
                  id="branch-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                  required
                />
              </div>
              <div>
                <label htmlFor="branch-code" className="mb-1.5 block text-sm font-medium">
                  Code
                </label>
                <input
                  id="branch-code"
                  type="text"
                  value={form.code}
                  onChange={(e) => updateField('code', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                  required
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label htmlFor="branch-currency" className="mb-1.5 block text-sm font-medium">
                  Currency
                </label>
                <select
                  id="branch-currency"
                  value={form.currency_code}
                  onChange={(e) => updateField('currency_code', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                >
                  {CURRENCIES.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="branch-country" className="mb-1.5 block text-sm font-medium">
                  Country
                </label>
                <input
                  id="branch-country"
                  type="text"
                  maxLength={2}
                  value={form.country}
                  onChange={(e) => updateField('country', e.target.value.toUpperCase())}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm uppercase',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                  required
                />
              </div>
              <div>
                <label htmlFor="branch-timezone" className="mb-1.5 block text-sm font-medium">
                  Timezone
                </label>
                <input
                  id="branch-timezone"
                  type="text"
                  value={form.timezone}
                  onChange={(e) => updateField('timezone', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                  required
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="branch-phone" className="mb-1.5 block text-sm font-medium">
                  Phone
                </label>
                <input
                  id="branch-phone"
                  type="text"
                  value={form.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder={PHONE_LOCAL_PLACEHOLDER}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                />
              </div>
              <div>
                <label htmlFor="branch-whatsapp" className="mb-1.5 block text-sm font-medium">
                  WhatsApp
                </label>
                <input
                  id="branch-whatsapp"
                  type="text"
                  value={form.whatsapp}
                  onChange={(e) => updateField('whatsapp', e.target.value)}
                  placeholder={PHONE_LOCAL_PLACEHOLDER}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                />
              </div>
            </div>

            <div>
              <label htmlFor="branch-address" className="mb-1.5 block text-sm font-medium">
                Address
              </label>
              <textarea
                id="branch-address"
                rows={2}
                value={form.address}
                onChange={(e) => updateField('address', e.target.value)}
                className={cn(
                  'w-full rounded-xl border border-border/80 bg-card px-4 py-3 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>

            <div>
              <p className="mb-3 text-sm font-medium">Business hours</p>
              <div className="space-y-2">
                {BUSINESS_DAYS.map((day) => (
                  <div key={day.key} className="grid grid-cols-[110px_1fr_1fr] items-center gap-3">
                    <span className="text-sm text-muted-foreground">{day.label}</span>
                    <input
                      type="time"
                      value={form.business_hours[day.key]?.open ?? ''}
                      onChange={(e) => updateHours(day.key, 'open', e.target.value)}
                      className={cn(
                        'h-10 rounded-xl border border-border/80 bg-card px-3 text-sm',
                        'focus:outline-none focus:ring-2 focus:ring-ring',
                      )}
                    />
                    <input
                      type="time"
                      value={form.business_hours[day.key]?.close ?? ''}
                      onChange={(e) => updateHours(day.key, 'close', e.target.value)}
                      className={cn(
                        'h-10 rounded-xl border border-border/80 bg-card px-3 text-sm',
                        'focus:outline-none focus:ring-2 focus:ring-ring',
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => updateField('is_active', e.target.checked)}
                className="rounded border-border"
              />
              Branch is active
            </label>

            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}

            {success ? (
              <p className="text-sm text-primary" role="status">
                {success}
              </p>
            ) : null}

            <Button type="submit" disabled={submitting || loading}>
              {submitting ? 'Saving…' : creating ? 'Create branch' : 'Save branch'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
